import React, { useEffect, useRef, useState } from "react";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import pxToRem from "~libs/pxToRem";
import { makeStyles } from "@mui/styles";
import ItemLoadingCard from "./ItemLoadingCard";
import IconEdit from "~assets/icons/ic_edit.svg";
import PhoneInput from "~styled/phone/PhoneInput";
import PhoneTipoSelector from "~styled/phone/PhoneTipoSelector";
import getArea from "~styled/phone/getArea";
import {
  PhoneField,
  toPhoneField,
  toPhoneFieldValue,
  withPhoneType,
} from "~styled/phone/phoneFormat";
import type { FieldChangeEvent } from "./types";

interface ItemPhoneCardProps {
  id?: string;
  label?: string;
  value?: string | null;
  onChange?: (event: FieldChangeEvent) => void;
  required?: boolean;
  displayError?: boolean;
  focus?: boolean;
  onBlur?: () => void;
  readOnly?: boolean;
  isLoading?: boolean;
  /** Para las pantallas que solo aceptan celulares: el tipo queda en MÓVIL y no se cambia. */
  onlyMobile?: boolean;
  /** El tipo de lo guardado cuando no tiene 54 ni 15 (el fijo de la póliza). */
  isMobile?: boolean;
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2%",
    borderBottom: "1px solid #8B8B8B",
  },
  caption: {
    fontFamily: "Nunito",
    fontSize: "16px",
    fontWeight: 600,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: 1.38,
    letterSpacing: "normal",
    textAlign: "left",
    color: "#8B8B8B",
  },
  body: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-start",
    maxWidth: pxToRem(270),
    width: "50%",
  },
  value: {
    fontFamily: "Nunito",
    fontSize: "16px",
    fontWeight: "bold",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: 1.38,
    letterSpacing: "normal",
    textAlign: "right",
    color: "#424242",
  },
  input: {
    padding: "12.5px 12px 12.5px 0",
    textAlign: "end",
    width: "100%",
  },
  buttonLink: {
    display: "flex",
    alignItems: "center",
    color: "#f77221",
    paddingLeft: 8,
    cursor: "pointer",
  },
  icon: { color: "#6240a5" },
  tipo: {
    height: 22,
    paddingRight: 10,
    marginRight: 0,
    borderRight: "1px solid #8B8B8B33",
  },
  outlinedRoot: {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#8B8B8B33" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#793CFF",
      borderWidth: 2,
    },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: "#f55050",
      borderWidth: 2,
    },
  },
}));

/**
 * El teléfono con el selector de tipo y la máscara del multicotizador. Lo que
 * no se puede interpretar (otro país, incompleto) se manda sin normalizar y no
 * bloquea el formulario.
 */
const ItemPhoneCard: React.FC<ItemPhoneCardProps> = ({
  id,
  label,
  value,
  onChange,
  required,
  displayError,
  focus,
  onBlur,
  readOnly,
  isLoading,
  onlyMobile,
  isMobile: defaultIsMobile = true,
}) => {
  const classes = useStyles();
  const reference = useRef<HTMLInputElement | null>(null);
  const [editing, setEditing] = useState(false);
  const [touched, setTouched] = useState(false);
  const [phone, setPhone] = useState<PhoneField>(() =>
    toPhoneField(value, onlyMobile, defaultIsMobile)
  );
  // Lo último que se mandó por onChange: cuando vuelve como value no se pisa
  // lo que se está escribiendo.
  const sent = useRef<string | null | undefined>(value);

  useEffect(() => {
    if (value === sent.current) return;
    sent.current = value;
    setPhone(toPhoneField(value, onlyMobile, defaultIsMobile));
  }, [value, onlyMobile, defaultIsMobile]);

  useEffect(() => {
    if (!isLoading && !readOnly && !value) setEditing(true);
  }, [isLoading, readOnly, value]);

  useEffect(() => {
    if (focus) reference.current?.focus();
  }, [focus]);

  const emit = (next: PhoneField) => {
    setPhone(next);
    const nextValue = toPhoneFieldValue(next);
    sent.current = nextValue;
    onChange?.({
      value: nextValue,
      valid: !required || !!nextValue,
      focus: false,
    });
  };

  const handleIsMobileChange = (event: { target: { value: boolean } }) => {
    emit(withPhoneType(phone, event.target.value));
    setTimeout(() => reference.current?.focus(), 0);
  };

  // PhoneInput avisa el tipo antes del onChange: se guarda para ese onChange.
  const detected = useRef<boolean | null>(null);

  const handleNumberChange = (event: { target: { value: string } }) => {
    const isMobile = detected.current ?? phone.isMobile;
    detected.current = null;
    // Borrar sobre el 15 no cambia nada: no cuenta como edición (el endoso
    // manda el teléfono solo si lo editan).
    if (event.target.value === phone.number && isMobile === phone.isMobile)
      return;
    emit({ number: event.target.value, isMobile });
  };

  const handleBlur = () => {
    setTouched(true);
    if (onBlur) onBlur();
  };

  if (isLoading) return <ItemLoadingCard label={label} />;

  const showError =
    required && !toPhoneFieldValue(phone) && (touched || displayError);
  const area = getArea(phone.number);

  return (
    <div className={classes.container}>
      <Typography className={classes.caption}>{label}</Typography>
      <div className={classes.body}>
        {editing && (
          <>
            <TextField
              id={id}
              style={{ width: "100%" }}
              autoComplete="off"
              type="tel"
              InputProps={{
                classes: { root: classes.outlinedRoot },
                inputComponent: PhoneInput as any,
                startAdornment: (
                  <InputAdornment position="start" className={classes.tipo}>
                    <PhoneTipoSelector
                      value={phone.isMobile}
                      onChange={handleIsMobileChange}
                      disabled={onlyMobile}
                      inline
                    />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                className: classes.input,
                isMobile: phone.isMobile,
                onIsMobileDetected: onlyMobile
                  ? undefined
                  : (isMobile: boolean) => {
                      detected.current = isMobile;
                    },
              }}
              onChange={handleNumberChange}
              onBlur={handleBlur}
              value={phone.number}
              variant="outlined"
              placeholder={
                phone.isMobile ? "(011) 15 2938-2938" : "(011) 2938-2938"
              }
              error={showError}
              helperText={
                showError ? "Requerido" : area ? area.Description : ""
              }
              inputRef={reference}
            />
          </>
        )}

        {!editing && (
          <>
            {value && (
              <Typography className={classes.value}>{value}</Typography>
            )}
            {!value && <div>&mdash;</div>}
            {!readOnly && (
              <div
                className={classes.buttonLink}
                onClick={() => setEditing(true)}
              >
                <img alt="" src={IconEdit} className={classes.icon} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ItemPhoneCard;
